import { useState } from 'react';
import { CabeceraPagina } from '../components/Armazon';
import {
  SelectorFranja,
  type Franja as FranjaSel,
} from '../components/SelectorFranja';
import { Aviso, Boton, Campo, Cargando, Tarjeta, Vacio } from '../components/ui';
import { ApiError, api, qs } from '../lib/api';
import { fechaHora, paraInput, ROL_ETIQUETA } from '../lib/formato';
import { useAvisos, useDatos, useSesion } from '../lib/sesion';
import type { Indisponibilidad } from '../lib/tipos';

export function Disponibilidad() {
  const { es } = useSesion();
  const { avisar } = useAvisos();
  const puedeEditar = es('COOK', 'SALES_MANAGER', 'ADMIN');

  const desde = new Date();
  desde.setMonth(desde.getMonth() - 1);
  const hasta = new Date();
  hasta.setMonth(hasta.getMonth() + 6);

  const { datos, cargando, error, recargar } = useDatos(
    (s) =>
      api.get<{ rows: Indisponibilidad[] }>(
        `/unavailability${qs({ from: desde.toISOString(), to: hasta.toISOString() })}`,
        s,
      ),
    [],
  );

  // Una hora concreta de un día, no el día entero: el selector amigable, el
  // mismo que se usa para elegir la franja de una visita.
  const [franja, setFranja] = useState<FranjaSel | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [fallo, setFallo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const rangoValido = Boolean(
    franja?.inicio && franja?.fin && new Date(franja.fin) > new Date(franja.inicio),
  );

  async function crear() {
    if (!franja) return;
    setGuardando(true);
    setFallo(null);
    try {
      const body = {
        start: new Date(franja.inicio).toISOString(),
        end: new Date(franja.fin).toISOString(),
        reason: motivo.trim(),
      };
      if (editando) await api.patch(`/unavailability/${editando}`, body);
      else await api.post('/unavailability', body);
      avisar(editando ? 'Bloqueo actualizado.' : 'Bloqueo registrado.', 'hecho');
      setEditando(null);
      setFranja(null);
      setMotivo('');
      recargar();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      <CabeceraPagina
        titulo="Bloqueos de cocina"
      />

      <div className="pila">
        {error && <Aviso tono="error">{error}</Aviso>}
        {fallo && <Aviso tono="error">{fallo}</Aviso>}

        {puedeEditar && (
          <Tarjeta titulo={editando ? "Editar bloqueo" : "Añadir bloqueo"}>
            <div className="pila">
              <SelectorFranja
                bloquearOcupadas
                excludeUnavailabilityId={editando ?? undefined}
                valor={franja}
                onCambio={setFranja}
              />
              <div className="fila" style={{ alignItems: 'flex-end' }}>
                <Campo etiqueta="Motivo">
                  <input
                    type="text"
                    value={motivo}
                    placeholder="Feria, mantenimiento, vacaciones…"
                    onChange={(e) => setMotivo(e.target.value)}
                    style={{ minWidth: 320 }}
                  />
                </Campo>
                <Boton
                  variante="clave"
                  cargando={guardando}
                  disabled={guardando || !rangoValido || motivo.trim().length < 3}
                  onClick={crear}
                >
                  Guardar
                </Boton>
                {editando && <Boton variante="secundario" onClick={() => { setEditando(null); setMotivo(''); setFranja(null); }}>Cancelar edición</Boton>}
              </div>
            </div>
          </Tarjeta>
        )}

        <Tarjeta titulo="Periodos registrados" sinRelleno>
          {cargando ? (
            <Cargando />
          ) : datos?.rows.length ? (
            <table className="tabla">
              <thead>
                <tr>
                  <th>Desde</th>
                  <th>Hasta</th>
                  <th>Motivo</th>
                  <th>Autor</th>
                  {puedeEditar && <th />}
                </tr>
              </thead>
              <tbody>
                {datos.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="dato" style={{ fontSize: 13 }}>{fechaHora(r.startAt)}</td>
                    <td className="dato" style={{ fontSize: 13 }}>{fechaHora(r.endAt)}</td>
                    <td>{r.reason}</td>
                    <td>Bloqueado por {r.createdBy.name} · {ROL_ETIQUETA[r.createdBy.role]}</td>
                    {puedeEditar && (
                      <td style={{ textAlign: 'right' }}>
                        {r.canEdit && <Boton variante="secundario" tam="s" onClick={() => {
                          setEditando(r.id); setMotivo(r.reason);
                          setFranja({ inicio: paraInput(r.startAt), fin: paraInput(r.endAt) });
                        }}>Editar</Boton>}
                        {r.canEdit && <Boton
                          variante="plano"
                          tam="s"
                          onClick={async () => {
                            setFallo(null);
                            try {
                              await api.del(`/unavailability/${r.id}`);
                              avisar('Bloqueo desactivado; se conserva el histórico.', 'hecho');
                              if (editando === r.id) { setEditando(null); setMotivo(''); }
                              recargar();
                            } catch (e) { setFallo(e instanceof ApiError ? e.message : 'No se ha podido desactivar.'); }
                          }}
                        >
                          Desactivar
                        </Boton>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Vacio titulo="Sin periodos registrados" />
          )}
        </Tarjeta>
      </div>
    </>
  );
}

